import React, { useEffect, useState } from "react";
import { apiGet } from "api/callApi";
import { Select, Checkbox, ConfigProvider, Tag } from "antd";
import theme from "theme";
import SC from "./style";
import { useSelector } from "react-redux";

const AlarmRecipientDropdown = ({
  onChange = () => {},
  defaultValue = "",
  name,
  disabledDropdown = false,
  placeholder = "Select Recipient",
  type,
  projectId,
  width = "100%",
  active,
  disabledValues = [],
  maxTagCount = 4,
}) => {
  const [options, setOptions] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [groupedOptions, setGroupedOptions] = useState({});
  const [selectedRoles, setSelectedRoles] = useState({});
  const [searchText, setSearchText] = useState("");

  const authData = useSelector((state) => state?.authdata?.data || null);

  useEffect(() => {
    const getData = async () => {
      try {
        let response = await apiGet(`/v1/addressbook/${projectId}/${type}`);

        let fetchedOptions = response.data.map((item) => ({
          id: item.id,
          label: item.fullName,
          email: item.email,
          roles: item.roles ? item.roles.split(",") : ["Others"],
          disabled: disabledValues.includes(item.id),
        }));

        // Check if authData should be added
        if (
          authData &&
          disabledValues.includes(authData.id) &&
          !fetchedOptions.some((opt) => opt.id === authData.id)
        ) {
          fetchedOptions = [
            ...fetchedOptions,
            {
              id: authData.id,
              label: authData.fullName || authData.label || "Unknown",
              email: authData.email,
              roles: ["Others"],
              disabled: true, // because it's in disabledValues
            },
          ];
        }

        setOptions(fetchedOptions);

        // Group users by roles
        const grouped = {};
        fetchedOptions.forEach((user) => {
          user.roles.forEach((role) => {
            if (!grouped[role]) grouped[role] = [];
            grouped[role].push(user);
          });
        });
        setGroupedOptions(grouped);

        // Sync defaultValue with fetched options
        if (defaultValue) {
          const initialSelected = fetchedOptions.filter((opt) =>
            (defaultValue || "").split(", ").includes(opt.email)
          );
          setSelectedValues(initialSelected);
        } else {
          setSelectedValues([]);
        }
      } catch (error) {
        console.error("Error fetching options:", error);
      }
    };

    getData();
  }, [projectId, type]);

  useEffect(() => {
    // Update role-based selection tracking after selectedValues change
    const updatedRoles = Object.keys(groupedOptions).reduce((acc, role) => {
      acc[role] = groupedOptions[role]?.every((user) =>
        selectedValues.some((selected) => selected.id === user.id)
      );
      return acc;
    }, {});
    setSelectedRoles(updatedRoles);
  }, [selectedValues, groupedOptions]);

  // Handle individual selection
  const handleOptionChange = (option, checked) => {
    if (option.disabled) return; // Prevent selection of disabled users

    const updatedValues = checked
      ? [...selectedValues, option]
      : selectedValues.filter((value) => value.id !== option.id);

    setSelectedValues(updatedValues);
    setSearchText("");

    const emailsString = updatedValues.map((value) => value.email).join(", ");
    onChange(name, emailsString, active);

    // Update role-based selection tracking
    const updatedRoles = Object.keys(groupedOptions).reduce((acc, role) => {
      const enabledUsers = groupedOptions[role].filter(
        (user) => !user.disabled
      );
      acc[role] = enabledUsers.every((user) =>
        updatedValues.some((selected) => selected.id === user.id)
      );
      return acc;
    }, {});

    setSelectedRoles(updatedRoles);
  };

  // Handle "Select All" per role
  const handleSelectAllByRole = (role, checked) => {
    let updatedValues = [...selectedValues];

    if (checked) {
      // Add only non-disabled users of this role
      const newSelections = groupedOptions[role].filter(
        (user) =>
          !user.disabled &&
          !selectedValues.some((selected) => selected.id === user.id)
      );
      updatedValues = [...updatedValues, ...newSelections];
    } else {
      // Remove only non-disabled users of this role
      updatedValues = updatedValues.filter(
        (user) =>
          !groupedOptions[role].some(
            (groupUser) => groupUser.id === user.id && !groupUser.disabled
          )
      );
    }

    setSelectedValues(updatedValues);

    const emailsString = updatedValues.map((value) => value.email).join(", ");
    onChange(name, emailsString, active);

    // Update role selection tracking
    setSelectedRoles((prev) => ({
      ...prev,
      [role]: checked,
    }));
  };

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchText.toLowerCase())
  );

  // Dynamically filter grouped options based on search
  const filteredGroupedOptions = Object.entries(groupedOptions).reduce(
    (acc, [role, users]) => {
      const filteredUsers = users.filter((user) =>
        user.label.toLowerCase().includes(searchText.toLowerCase())
      );
      if (filteredUsers.length > 0) {
        acc[role] = filteredUsers;
      }
      return acc;
    },
    {}
  );

  const handleSearch = (value) => {
    setSearchText(value); // Update search value dynamically
  };

  const renderCheckboxList = () =>
    // Object.entries(filteredGroupedOptions).map(([role, users]) => (
    Object.entries(filteredGroupedOptions)
      .sort(([roleA], [roleB]) => roleA.localeCompare(roleB))
      .map(([role, users]) => (
        <div key={role} style={{ padding: "2px 0" }}>
          <Checkbox
            checked={selectedRoles[role] || false}
            onChange={(e) => handleSelectAllByRole(role, e.target.checked)}
            style={{
              fontWeight: "bold",
              margin: "4px 0",
              paddingLeft: "15px",
              display: "flex",
              backgroundColor: "#CCCCCC",
              padding: "2px 2px 2px 15px",
              opacity: "0.8",
            }}
          >
            {role}
          </Checkbox>
          {users.map((option) => (
            <Checkbox
              key={option.id}
              checked={selectedValues.some((value) => value.id === option.id)}
              disabled={option?.disabled || false}
              onChange={(e) => handleOptionChange(option, e.target.checked)}
              style={{ display: "flex", margin: "4px 0", paddingLeft: "15px" }}
            >
              {option.label}
            </Checkbox>
          ))}
        </div>
      ));

  const customTagRender = (props) => {
    const { label, value, closable, onClose } = props;

    const isDisabled = options.find(
      (option) => option.email === value
    )?.disabled;
    if (isDisabled) {
      return <Tag color="#BFBFBF">{label}</Tag>;
    }

    return (
      <Tag closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
        {label}
      </Tag>
    );
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#ED850C",
          colorBorder: theme.colors.bordertask,
          colorText: "black",
          colorTextPlaceholder: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <Select
        showSearch
        filterOption={false}
        onSearch={handleSearch}
        optionFilterProp="children"
        mode="multiple"
        value={selectedValues.map((value) => value.email)}
        onChange={(selectedEmails) => {
          const updatedValues = filteredOptions.filter((option) =>
            selectedEmails.includes(option.email)
          );
          setSelectedValues(updatedValues);
          onChange(name, updatedValues.map((v) => v.email).join(", "), active);
        }}
        tagRender={customTagRender}
        style={{ width: width }}
        placeholder={placeholder}
        dropdownRender={(menu) =>
          filteredOptions.length === 0 ? (
            <div
              style={{ padding: "10px", textAlign: "center", color: "#888" }}
            >
              No Data
            </div>
          ) : (
            <SC.ChildContainer>{renderCheckboxList()}</SC.ChildContainer>
          )
        }
        disabled={disabledDropdown}
        maxTagCount={maxTagCount}
      >
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <Select.Option
              key={option.id}
              value={option.email}
              // disabled={option.disabled}
              label={option.label}
            >
              {option.label}
            </Select.Option>
          ))
        ) : (
          <Select.Option disabled value="no-data">
            No Data
          </Select.Option>
        )}
      </Select>
    </ConfigProvider>
  );
};

export default AlarmRecipientDropdown;
